'use client';

import { useEffect } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import Icon from '@/components/ui/AppIcon';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcuts = [
  { category: 'Navigation', items: [
    { keys: ['Tab'], description: 'Move to next interactive element' },
    { keys: ['Shift', 'Tab'], description: 'Move to previous element' },
    { keys: ['Enter'], description: 'Activate focused element' },
    { keys: ['Space'], description: 'Toggle buttons and checkboxes' },
    { keys: ['↑', '↓'], description: 'Navigate dropdown options' },
  ]},
  { category: 'Global Actions', items: [
    { keys: ['Ctrl', 'K'], description: 'Open search' },
    { keys: ['Ctrl', 'N'], description: 'New use case' },
    { keys: ['Ctrl', 'S'], description: 'Save current form' },
    { keys: ['Esc'], description: 'Close modal / Cancel' },
    { keys: ['?'], description: 'Show this help panel' },
  ]},
  { category: 'Accessibility', items: [
    { keys: ['Skip Link'], description: 'Jump to main content (Tab from top)' },
    { keys: ['Focus Ring'], description: 'Gold ring shows keyboard focus' },
  ]},
];

export default function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const containerRef = useFocusTrap(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center p-4"
      style={{ animation: 'backdropFadeIn 200ms ease-out forwards' }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-modal-title"
        className="relative w-full max-w-lg rounded-xl bg-card border border-border shadow-elevation-4"
        style={{ animation: 'modalScaleIn 200ms ease-out forwards' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-accent/10">
              <Icon name="CommandLineIcon" size={18} className="text-accent" />
            </div>
            <h2
              id="shortcuts-modal-title"
              className="font-heading text-lg font-semibold text-foreground"
            >
              Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
            className="rounded-md p-1.5 text-muted-foreground transition-all duration-200 hover:bg-muted hover:text-foreground focus:outline-none focus-gold"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-5">
          {shortcuts.map((section) => (
            <div key={section.category}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">
                {section.category}
              </h3>
              <div className="space-y-2">
                {section.items.map((item) => (
                  <div
                    key={item.description}
                    className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
                  >
                    <span className="text-sm text-foreground">{item.description}</span>
                    <div className="flex items-center gap-1">
                      {item.keys.map((key, i) => (
                        <span key={i} className="flex items-center gap-1">
                          <kbd className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded border border-border bg-muted px-1.5 font-mono text-xs text-foreground shadow-sm">
                            {key}
                          </kbd>
                          {i < item.keys.length - 1 && (
                            <span className="text-xs text-muted-foreground">+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-3">
          <p className="text-xs text-muted-foreground text-center">
            Press <kbd className="inline-flex h-5 items-center rounded border border-border bg-muted px-1 font-mono text-xs">?</kbd> anywhere to toggle this panel
          </p>
        </div>
      </div>
    </div>
  );
}
